import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  commentInboxService,
  type InboxDismissReason,
  type InboxFilters,
  type InboxItem,
  type InboxSignature,
  type InboxThread,
} from '@/services/comment-inbox.service';
import { commentKeys } from './useComments';

export const inboxKeys = {
  all: ['comment-inbox'] as const,
  lists: () => [...inboxKeys.all, 'list'] as const,
  list: (filters: InboxFilters) => [...inboxKeys.lists(), filters] as const,
  summary: () => [...inboxKeys.all, 'summary'] as const,
  thread: (commentId: string) =>
    [...inboxKeys.all, 'thread', commentId] as const,
};

/** Contadores da caixa; o menu do admin mostra as pendentes. */
export function useInboxSummary(enabled = true) {
  return useQuery({
    queryKey: inboxKeys.summary(),
    queryFn: commentInboxService.summary,
    refetchInterval: 30_000,
    enabled,
  });
}

export function useInboxList(filters: InboxFilters) {
  return useInfiniteQuery({
    queryKey: inboxKeys.list(filters),
    queryFn: ({ pageParam }) => commentInboxService.list(filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.page < last.meta.totalPages ? last.meta.page + 1 : undefined,
  });
}

export function useInboxThread(commentId: string | undefined) {
  return useQuery({
    queryKey: inboxKeys.thread(commentId ?? ''),
    queryFn: () => commentInboxService.thread(commentId as string),
    enabled: !!commentId,
  });
}

/**
 * Toda ação muda a lista e os contadores; a conversa aberta recebe o estado
 * novo na hora. Em erro (409: alguém da equipe agiu antes) tudo é relido.
 */
function useInboxAction<TVariables extends { commentId: string }>(
  action: (variables: TVariables) => Promise<InboxThread | InboxItem>,
) {
  const queryClient = useQueryClient();
  const refresh = (commentId: string) => {
    queryClient.invalidateQueries({ queryKey: inboxKeys.lists() });
    queryClient.invalidateQueries({ queryKey: inboxKeys.summary() });
    queryClient.invalidateQueries({ queryKey: inboxKeys.thread(commentId) });
  };
  return useMutation({
    mutationFn: action,
    onSuccess: (result, { commentId }) => {
      if ('replies' in result) {
        queryClient.setQueryData(inboxKeys.thread(commentId), result);
        queryClient.invalidateQueries({ queryKey: commentKeys.all });
      } else {
        queryClient.setQueryData<InboxThread>(
          inboxKeys.thread(commentId),
          (thread) => thread && { ...thread, item: result },
        );
      }
      queryClient.invalidateQueries({ queryKey: inboxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: inboxKeys.summary() });
    },
    onError: (_error, { commentId }) => refresh(commentId),
  });
}

export function useReplyInbox() {
  return useInboxAction(
    ({ commentId, ...body }: { commentId: string; content: string; signature: InboxSignature }) =>
      commentInboxService.reply(commentId, body),
  );
}

export function useDismissInbox() {
  return useInboxAction(
    ({ commentId, ...body }: { commentId: string; reason: InboxDismissReason; note?: string }) =>
      commentInboxService.dismiss(commentId, body),
  );
}

export function useResolveInbox() {
  return useInboxAction(({ commentId }: { commentId: string }) =>
    commentInboxService.resolve(commentId),
  );
}

export function useReopenInbox() {
  return useInboxAction(({ commentId }: { commentId: string }) =>
    commentInboxService.reopen(commentId),
  );
}
